import { Plus, Trash2 } from 'lucide-react';
import { TagInput } from '../../components/TagInput';
import { IconButton } from '../../components/IconButton';
import type { IssueField, IssueForm } from '../../domain/types';
import { createUid, normalizeStringList } from '../../domain/utils';
import type { TranslationKey } from '../../i18n';
import ui from '../../components/ui.module.css';
import styles from './InspectorPanel.module.css';

interface InspectorPanelProps {
  form: IssueForm;
  selectedField: IssueField | null;
  t: (key: TranslationKey, values?: Record<string, string | number>) => string;
  onFormChange: (form: IssueForm) => void;
  onFieldChange: (field: IssueField) => void;
}

function FormInspector({
  form,
  t,
  onChange,
}: {
  form: IssueForm;
  t: InspectorPanelProps['t'];
  onChange: (form: IssueForm) => void;
}) {
  return (
    <div className={styles.formGrid}>
      <label className={styles.field}>
        <span>{t('filename')}</span>
        <input
          value={form.filename}
          data-testid="filename-input"
          spellCheck={false}
          onChange={(event) => onChange({ ...form, filename: event.target.value })}
        />
      </label>
      <label className={styles.field}>
        <span>{t('name')}</span>
        <input
          value={form.name}
          onChange={(event) => onChange({ ...form, name: event.target.value })}
        />
      </label>
      <label className={styles.field}>
        <span>{t('description')}</span>
        <textarea
          rows={3}
          value={form.description}
          onChange={(event) => onChange({ ...form, description: event.target.value })}
        />
      </label>
      <label className={styles.field}>
        <span>{t('title')}</span>
        <input
          value={form.title}
          onChange={(event) => onChange({ ...form, title: event.target.value })}
        />
      </label>
      <div className={styles.field}>
        <span>{t('labels')}</span>
        <TagInput
          values={form.labels}
          label={t('labels')}
          placeholder={t('addValue')}
          removeLabel={(value) => t('removeValue', { value })}
          onChange={(labels) => onChange({ ...form, labels: normalizeStringList(labels) })}
        />
      </div>
      <div className={styles.field}>
        <span>{t('assignees')}</span>
        <TagInput
          values={form.assignees}
          label={t('assignees')}
          placeholder={t('addValue')}
          removeLabel={(value) => t('removeValue', { value })}
          onChange={(assignees) => onChange({ ...form, assignees: normalizeStringList(assignees) })}
        />
      </div>
    </div>
  );
}

function TextFieldInspector({
  field,
  t,
  onChange,
}: {
  field: Exclude<IssueField, { type: 'markdown' }>;
  t: InspectorPanelProps['t'];
  onChange: (field: IssueField) => void;
}) {
  const patchAttributes = (attributes: Partial<typeof field.attributes>) =>
    onChange({ ...field, attributes: { ...field.attributes, ...attributes } } as IssueField);

  return (
    <div className={styles.formGrid}>
      <div className={styles.readonlyType}>
        <span>{t('fieldType')}</span>
        <code>{field.type}</code>
      </div>
      <label className={styles.field}>
        <span>{t('id')}</span>
        <input
          value={field.id}
          spellCheck={false}
          onChange={(event) => onChange({ ...field, id: event.target.value } as IssueField)}
        />
      </label>
      <label className={styles.field}>
        <span>{t('label')}</span>
        <input
          value={field.attributes.label}
          onChange={(event) => patchAttributes({ label: event.target.value })}
        />
      </label>
      <label className={styles.field}>
        <span>{t('description')}</span>
        <textarea
          rows={2}
          value={field.attributes.description}
          onChange={(event) => patchAttributes({ description: event.target.value })}
        />
      </label>

      {field.type === 'input' || field.type === 'textarea' ? (
        <label className={styles.field}>
          <span>{t('placeholder')}</span>
          <input
            value={field.attributes.placeholder}
            onChange={(event) => patchAttributes({ placeholder: event.target.value })}
          />
        </label>
      ) : null}

      {field.type === 'input' || field.type === 'textarea' ? (
        <label className={styles.field}>
          <span>{t('defaultValue')}</span>
          <textarea
            rows={field.type === 'textarea' ? 3 : 1}
            value={field.attributes.value}
            onChange={(event) => patchAttributes({ value: event.target.value })}
          />
        </label>
      ) : null}

      {field.type === 'textarea' ? (
        <label className={styles.field}>
          <span>{t('render')}</span>
          <input
            value={field.attributes.render}
            placeholder="shell"
            spellCheck={false}
            onChange={(event) => patchAttributes({ render: event.target.value })}
          />
        </label>
      ) : null}

      {field.type === 'dropdown' ? (
        <div className={styles.optionSection}>
          <div className={styles.optionHeading}>
            <span>{t('options')}</span>
            <button
              type="button"
              className={ui.button}
              onClick={() => patchAttributes({ options: [...field.attributes.options, ''] })}
            >
              <Plus size={14} strokeWidth={1.8} aria-hidden="true" />
              {t('addOption')}
            </button>
          </div>
          {field.attributes.options.map((option, index) => (
            <div className={styles.optionRow} key={index}>
              <label className={styles.field}>
                <span>{t('option', { index: index + 1 })}</span>
                <input
                  value={option}
                  onChange={(event) => {
                    const options = [...field.attributes.options];
                    options[index] = event.target.value;
                    patchAttributes({ options });
                  }}
                />
              </label>
              <IconButton
                icon={Trash2}
                label={t('removeOption', { index: index + 1 })}
                onClick={() =>
                  patchAttributes({
                    options: field.attributes.options.filter(
                      (_, optionIndex) => optionIndex !== index,
                    ),
                  })
                }
              />
            </div>
          ))}
          <label className={styles.checkField}>
            <input
              type="checkbox"
              checked={field.attributes.multiple}
              onChange={(event) => patchAttributes({ multiple: event.target.checked })}
            />
            {t('multiple')}
          </label>
        </div>
      ) : null}

      {field.type === 'checkboxes' ? (
        <div className={styles.optionSection}>
          <div className={styles.optionHeading}>
            <span>{t('options')}</span>
            <button
              type="button"
              className={ui.button}
              onClick={() =>
                patchAttributes({
                  options: [
                    ...field.attributes.options,
                    { uid: createUid('option'), label: '', required: false },
                  ],
                })
              }
            >
              <Plus size={14} strokeWidth={1.8} aria-hidden="true" />
              {t('addOption')}
            </button>
          </div>
          {field.attributes.options.map((option, index) => (
            <div className={styles.checkboxOption} key={option.uid}>
              <label className={styles.field}>
                <span>{t('option', { index: index + 1 })}</span>
                <input
                  value={option.label}
                  onChange={(event) => {
                    const options = field.attributes.options.map((item) =>
                      item.uid === option.uid ? { ...item, label: event.target.value } : item,
                    );
                    patchAttributes({ options });
                  }}
                />
              </label>
              <label className={styles.checkField}>
                <input
                  type="checkbox"
                  checked={option.required}
                  onChange={(event) => {
                    const options = field.attributes.options.map((item) =>
                      item.uid === option.uid ? { ...item, required: event.target.checked } : item,
                    );
                    patchAttributes({ options });
                  }}
                />
                {t('required')}
              </label>
              <IconButton
                icon={Trash2}
                label={t('removeOption', { index: index + 1 })}
                onClick={() =>
                  patchAttributes({
                    options: field.attributes.options.filter((item) => item.uid !== option.uid),
                  })
                }
              />
            </div>
          ))}
        </div>
      ) : null}

      {'validations' in field ? (
        <label className={styles.checkField}>
          <input
            type="checkbox"
            checked={field.validations.required}
            onChange={(event) =>
              onChange({
                ...field,
                validations: { required: event.target.checked },
              } as IssueField)
            }
          />
          {t('required')}
        </label>
      ) : null}
    </div>
  );
}

export function InspectorPanel({
  form,
  selectedField,
  t,
  onFormChange,
  onFieldChange,
}: InspectorPanelProps) {
  return (
    <section className={styles.inspector} data-testid="inspector-panel">
      <header className={styles.header}>
        <div>
          <span>{selectedField ? t('selectedField') : t('formSettings')}</span>
          <strong>{selectedField ? fieldInspectorTitle(selectedField) : form.filename}</strong>
        </div>
      </header>
      <div className={styles.body}>
        {selectedField ? (
          selectedField.type === 'markdown' ? (
            <div className={styles.formGrid}>
              <div className={styles.readonlyType}>
                <span>{t('fieldType')}</span>
                <code>markdown</code>
              </div>
              <label className={styles.field}>
                <span>{t('markdownValue')}</span>
                <textarea
                  rows={12}
                  value={selectedField.attributes.value}
                  onChange={(event) =>
                    onFieldChange({
                      ...selectedField,
                      attributes: { value: event.target.value },
                    })
                  }
                />
              </label>
            </div>
          ) : (
            <TextFieldInspector field={selectedField} t={t} onChange={onFieldChange} />
          )
        ) : (
          <FormInspector form={form} t={t} onChange={onFormChange} />
        )}
      </div>
    </section>
  );
}

function fieldInspectorTitle(field: IssueField): string {
  if (field.type === 'markdown') return 'Markdown';
  return field.attributes.label || field.id || field.type;
}
